import { useEffect, useState } from 'react'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate
} from 'react-router-dom'
import { getCookieConsentValue } from 'react-cookie-consent'
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core'
import * as Sentry from '@sentry/react'
import './App.scss'
import environment from './environment'
import * as routes from './routes'
import globals from './globals'
import { AlertContextProvider } from './widgets'
import { PrivateRoute } from './auth'
import { Loading } from './shared'
import {
  CookieConsentBar,
  PublicBeatView,
  PublicPostView,
  PublicPlaylistView,
} from './components'
import {
  ProducerSignUp,
  ProducerLogin,
  ProducerBeats,
  ProducerPosts,
  ProducerPlaylists,
  ProducerProfileEdit,
  ProducerProfile,
  BeatUploadsMovingToBeatstars,
  EditBeat,
  PublicUserView,
  Report,
  PurchaseBeatBoost,
  ProducerBoosts,
  EditPlaylist,
  ConversationListView,
  ConversationView,
  ConversationsNewMessageView,
} from './user'
import {
  AdminLogin,
  AdminEditBeat,
  AdminEditPost,
  AdminEditHashtag,
  AdminEditPlaylist,
  AdminCreatorProfile,
  AdminSearch,
  AdminDiscover,
  AdminNewDiscoverItem,
  AdminModeration,
  AdminAnalytics,
  AdminConversation,
} from './admin'
import { AudioPlayerContextProvider } from './components/AudioPlayerContext'
import {
  AdminBeats,
  AdminPosts,
  AdminPlaylists,
  AdminHashtags,
  AdminBoosts,
  AdminProjects,
} from './admin/feed'
import { ProducerNavigation } from './user/components'
import { AdminNavigation } from './admin/components'

const PublicView = ({element}) => <>
  <AudioPlayerContextProvider>
    {element}
  </AudioPlayerContextProvider>
</>

const PrivateProducer = ({element}) => <PrivateRoute
  element={
    <>
      <ProducerNavigation />
      <AudioPlayerContextProvider withNavigationSidebar>
        {element}
      </AudioPlayerContextProvider>
    </>
  }
  loginPath={routes.producerSignInUrl}
  unauthedPath={routes.producerRootPath}
/>

const PrivateAdmin = ({element}) => <PrivateRoute
  element={
    <>
      <AdminNavigation />
      <AudioPlayerContextProvider withNavigationSidebar>
        {element}
      </AudioPlayerContextProvider>
    </>
  }
  loginPath={routes.adminAuthUrl}
  unauthedPath={routes.adminRootPath}
/>

const App = () => {
  const [loading, setLoading] = useState(true)

  const sentryEnvironment = () => {
    var env = environment()
    if (env === 'prod') {
      return 'production'
    } else if (env === 'qa') {
      return 'qa'
    }
    return 'development'
  }

  const initSentry = () => {
    if (environment() === 'dev') {
      return
    }
    Sentry.init({
      dsn: 'https://e147eedf1782492db3042b4e381fb9e9@o407839.ingest.sentry.io/5391059',
      environment: sentryEnvironment(),
      ignoreErrors: [
        // Custom ignores
        // VidMate app
        /vid_mate_/,
        // HeyTap browser
        'getReadMode',

        // These are from https://gist.github.com/impressiver/5092952
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        "Can't find variable: ZiteReader",
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP 'optimizing' proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Generic error code from errors outside the security sandbox
        // You can delete this if using raven.js > 1.0, which ignores these automatically.
        'Script error.',
      ],
      ignoreUrls: [
        // These are from https://gist.github.com/impressiver/5092952
        // Facebook flakiness
        /graph\.facebook\.com/i,
        // Facebook blocked
        /connect\.facebook\.net\/en_US\/all\.js/i,
        // Woopra flakiness
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com\/js\/woopra\.js/i,
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i,  // Cacaoweb
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
      ]
    })
  }

  const consentCookieName = 'consent-cookie'

  useEffect(() => {
    globals.load((error) => {
      if (error) {
        console.log(error)
        alert(error)
      } else {
        setLoading(false)
      }
    })

    var cookieConsentGiven = getCookieConsentValue(consentCookieName) === 'accepted'

    var mixpanelProjectToken = null
    if (environment() === 'prod') {
      mixpanelProjectToken = 'cb56acba1f1f75b74d844089b03e2211'
    } else {
      mixpanelProjectToken = '218613c0ca6b6d012b3a7f0b40bb2012'
    }
    // only enable page view events for now
    // https://docs.mixpanel.com/docs/tracking-methods/sdks/javascript#autocapture
    mixpanel.init(mixpanelProjectToken, {
      autocapture: {
        pageview: 'url-with-path',
        click: false,
        input: false,
        scroll: false,
        submit: false,
      },
      opt_out_tracking_by_default: !cookieConsentGiven
    })

    if (cookieConsentGiven) {
      initSentry()
    }
  }, [])

  const onCookieConsentAccept = () => {
    mixpanel.opt_in_tracking()
    initSentry()
  }

  return (
    <Router>
      <AlertContextProvider>
        <div className='content'>
          <CookieConsentBar onAccept={onCookieConsentAccept} cookieName={consentCookieName} />
          {loading ? <Loading /> :
            <Routes>

              {/* PRODUCER  */}
              <Route path={routes.rootPath} element={<Navigate to={routes.producerRootPath} />} />
              <Route path={`${routes.rootPath}index.html`} element={<Navigate to={routes.producerRootPath} />} />
              <Route path={routes.producerRootPath} element={<ProducerLogin/>} />
              <Route path={routes.producerSignUpPath} element={<ProducerSignUp/>} />

              <Route path={routes.producerBeatsPath} element={<PrivateProducer element={<ProducerBeats/>} />} />
              <Route path={routes.producerTopTracksPath} element={<PrivateProducer element={<ProducerPosts/>} />} />
              <Route path={routes.producerPlaylistsPath} element={<PrivateProducer element={<ProducerPlaylists/>} />} />
              <Route path={routes.producerSubmitPath} element={<PrivateProducer element={<BeatUploadsMovingToBeatstars/>} />} />
              <Route path={routes.producerBeatsSubmitPath} element={<PrivateProducer element={<BeatUploadsMovingToBeatstars/>} />} />
              <Route path={routes.producerBeatsEditPath} element={<PrivateProducer element={<EditBeat/>} />} />
              <Route path={routes.producerPlaylistsEditPath} element={<PrivateProducer element={<EditPlaylist/>} />} />
              <Route path={routes.producerProfileEditPath} element={<PrivateProducer element={<ProducerProfileEdit/>} />} />
              <Route path={routes.producerProfilePath} element={<PrivateProducer element={<ProducerProfile/>} />} />
              <Route path={routes.producerPurchaseBeatBoostPath} element={<PrivateProducer element={<PurchaseBeatBoost/>} />} />
              <Route path={routes.producerBeatUploadsMovingToBeatstars} element={<PrivateProducer element={<BeatUploadsMovingToBeatstars/>} />} />
              <Route path={routes.producerBoostsPath} element={<PrivateProducer element={<ProducerBoosts/>} />} />
              <Route path={routes.producerReportPath} element={<Report/>} />
              <Route path={routes.producerConversationListPath} element={<PrivateProducer element={<ConversationListView/>} />} />
              <Route path={routes.producerConversationPath} element={<PrivateProducer element={<ConversationView/>} />} />
              <Route path={routes.producerNewMessagePath} element={<PrivateProducer element={<ConversationsNewMessageView/>} />} />

              <Route path={routes.withoutParams(routes.purchaseBoostDeepLink)} element={<PrivateProducer element={<PurchaseBeatBoost/>} />} />

            {/* ADMIN */}
              <Route path={routes.adminRootPath} element={<AdminLogin/>} />
              <Route path={routes.adminBeatsPath} element={<PrivateAdmin element={<AdminBeats/>} />} />
              <Route path={routes.adminPostsPath} element={<PrivateAdmin element={<AdminPosts/>} />} />
              <Route path={routes.adminPlaylistsPath} element={<PrivateAdmin element={<AdminPlaylists/>} />} />
              <Route path={routes.adminHashtagsPath} element={<PrivateAdmin element={<AdminHashtags/>} />} />
              <Route path={routes.adminProjectsPath} element={<PrivateAdmin element={<AdminProjects/>} />} />
              <Route path={routes.adminCreatorProfilePath} element={<PrivateAdmin element={<AdminCreatorProfile/>} />} />
              <Route path={routes.adminSearchPath} element={<PrivateAdmin element={<AdminSearch/>} />} />
              <Route path={routes.adminModerationPath} element={<PrivateAdmin element={<AdminModeration/>} />} />
              <Route path={routes.adminDiscoverPath} element={<PrivateAdmin element={<AdminDiscover/>} />} />
              <Route path={routes.adminDiscoverNewPath} element={<PrivateAdmin element={<AdminNewDiscoverItem/>} />} />
              <Route path={routes.adminPostEditPath} element={<PrivateAdmin element={<AdminEditPost/>} />} />
              <Route path={routes.adminBeatsEditPath} element={<PrivateAdmin element={<AdminEditBeat/>} />} />
              <Route path={routes.adminPlaylistEditPath} element={<PrivateAdmin element={<AdminEditPlaylist/>} />} />
              <Route path={routes.adminHashtagEditPath} element={<PrivateAdmin element={<AdminEditHashtag/>} />} />
              <Route path={routes.adminAnalyticsPath} element={<PrivateAdmin element={<AdminAnalytics/>} />} />
              <Route path={routes.adminBoostListPath} element={<PrivateAdmin element={<AdminBoosts/>} />} />
              <Route path={routes.adminConversationPath} element={<PrivateAdmin element={<AdminConversation/>} />} />

              {/* PUBLIC */}
              <Route path={routes.applinksPostsPath} element={<PublicView element={<PublicPostView/>} />} />
              <Route path={routes.applinksBeatsPath} element={<PublicView element={<PublicBeatView/>} />} />
              <Route path={routes.applinksPublicUserProfilePath} element={<PublicView element={<PublicUserView/>} />} />
              <Route path={routes.applinksPlaylistPath} element={<PublicView element={<PublicPlaylistView/>} />} />

              {/* CATCH ALL */}
              <Route
                path='*'
                element={
                  <>
                    <div className='welcome-title'>404</div>
                    <div>There&apos;s nothing here</div>
                  </>
                }
              />
            </Routes>
          }
        </div>
      </AlertContextProvider>
    </Router>
  )
}

export default App
